import { PEN_RECORDS_UPDATED_EVENT } from '@/utils/penRecords'
import {
  ensurePenRecords,
  getCurrentUserId,
  persistPenRecords
} from '@/utils/penBridge/recordStore'

const PEN_TYPE = 'wzb'
const BLE_TYPE = 'EPEN_TYPE'
const MIN_RECORD_YEAR = 2026

function safeJsonParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (error) {
    return fallback
  }
}

function add0(value) {
  return String(value).padStart(2, '0')
}

function emitUpdate(detail) {
  if (typeof window === 'undefined') return

  let event
  if (typeof window.CustomEvent === 'function') {
    event = new window.CustomEvent(PEN_RECORDS_UPDATED_EVENT, { detail })
  } else {
    event = document.createEvent('CustomEvent')
    event.initCustomEvent(PEN_RECORDS_UPDATED_EVENT, false, false, detail)
  }
  window.dispatchEvent(event)
}

function getDeviceStatus() {
  const uid = getCurrentUserId()
  const scopedStatus = safeJsonParse(localStorage.getItem('BTdeviceStatus_' + PEN_TYPE), {})
  if (Object.keys(scopedStatus).length && (!uid || String(scopedStatus.uid) === String(uid))) {
    return scopedStatus
  }

  const legacyStatus = safeJsonParse(localStorage.getItem('BTdeviceStatus'), {})
  if ((!uid || String(legacyStatus.uid) === String(uid)) && legacyStatus.name === PEN_TYPE) {
    return legacyStatus
  }
  return {}
}

function saveDeviceStatus(patch) {
  const uid = getCurrentUserId()
  const deviceStatus = {
    ...getDeviceStatus(),
    ...patch,
    name: PEN_TYPE,
    uid
  }
  if (uid) localStorage.setItem('BTpenCacheUid', String(uid))
  localStorage.setItem('BTdeviceStatus_' + PEN_TYPE, JSON.stringify(deviceStatus))
  localStorage.setItem('BTdeviceStatus', JSON.stringify(deviceStatus))
  emitUpdate({ penType: PEN_TYPE, deviceStatus, loading: false })
}

function bytesToHex(data) {
  if (!Array.isArray(data)) return ''
  return data.map(item => ((item + 256) & 0xff).toString(16).padStart(2, '0')).join('')
}

function inferRequestHex(hex) {
  if (hex.startsWith('5a55fd')) return 'fdff'
  if (hex.startsWith('f6')) return 'f5'
  if (hex.includes('f8ffff')) return 'f7'
  if (hex.includes('5a55fa')) return 'f9'
  if (hex.length === 18) {
    const index = parseInt(hex.substring(0, 2), 16) + 1
    return 'fd' + index.toString(16).padStart(2, '0')
  }
  return ''
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '--'
  const date = new Date(seconds * 1000)
  return (
    date.getFullYear() +
    '-' +
    add0(date.getMonth() + 1) +
    '-' +
    add0(date.getDate()) +
    ' ' +
    add0(date.getHours()) +
    ':' +
    add0(date.getMinutes()) +
    ':' +
    add0(date.getSeconds())
  )
}

function parseInjectionRecord(hex) {
  if (hex.length !== 18) return null
  const seconds = parseInt(hex.substring(6, 14), 16)
  const doseRaw = parseInt(hex.substring(14, 18), 16)
  if (!Number.isFinite(seconds) || !Number.isFinite(doseRaw)) return null
  const recordTime = new Date(seconds * 1000)
  const jiexiTime = recordTime.getFullYear() < MIN_RECORD_YEAR ? formatTime(Math.floor(Date.now() / 1000)) : ''
  return {
    createTime: formatTime(seconds),
    realInsulin: (doseRaw & 0x7fff) / 10,
    valid: (doseRaw & 0x8000) !== 0,
    ...(jiexiTime ? { jiexiTime } : {})
  }
}

function finishRecordResponse(records) {
  emitUpdate({
    penType: PEN_TYPE,
    loading: false,
    ...(Array.isArray(records) ? { insulinRecordList: records } : {})
  })
}

function handleRecord(hex, empty, requestHex) {
  if (empty) {
    finishRecordResponse()
    return
  }

  const record = parseInjectionRecord(hex)
  if (!record || !record.valid) {
    finishRecordResponse()
    return
  }

  persistPenRecords(
    [
      {
        createTime: record.createTime,
        realInsulin: record.realInsulin,
        requestHex,
        responseHex: hex,
        ...(record.jiexiTime ? { jiexiTime: record.jiexiTime } : {})
      }
    ],
    PEN_TYPE
  )
    .then(finishRecordResponse)
    .catch(error => {
      console.log('wzb record save failed', error)
      finishRecordResponse()
    })
}

export function matchesWzbPenResponse(obj) {
  if (!obj || !Array.isArray(obj.data)) return false
  if (obj.bleType === BLE_TYPE) return true
  const responseHex = bytesToHex(obj.data)
  const requestHex = String(obj.requestHex || inferRequestHex(responseHex)).toLowerCase()
  return /^(f5|f7|f9|fd[0-9a-f]{2})$/.test(requestHex)
}

export async function handleWzbPenResponse(obj) {
  const responseHex = bytesToHex(obj.data)
  const requestHex = String(obj.requestHex || inferRequestHex(responseHex)).toLowerCase()

  if (requestHex === 'f5') {
    if (responseHex.startsWith('f6') && responseHex.length >= 10) {
      saveDeviceStatus({ createTime: formatTime(parseInt(responseHex.substring(2, 10), 16)) })
    }
    return
  }
  if (requestHex === 'f7') {
    if (responseHex.includes('f8ffff') && responseHex.length >= 12) {
      const doseTypeHex = responseHex.substring(responseHex.length - 12, responseHex.length - 8)
      const first = parseInt(doseTypeHex.substring(0, 2), 16)
      const second = parseInt(doseTypeHex.substring(2, 4), 16)
      const insulinTypeCode = (first & 0xc0) >> 6
      saveDeviceStatus({
        leftInsulin: (((first & 0x3f) << 8) | second) / 10,
        medicalType: insulinTypeCode === 2 ? 'Novo' : insulinTypeCode === 3 ? 'Other' : 'Unknown'
      })
    }
    return
  }
  if (requestHex === 'f9') {
    if (responseHex.includes('5a55fa') && responseHex.length >= 8) {
      saveDeviceStatus({ battery: parseInt(responseHex.substring(6), 16) })
    }
    return
  }
  if (requestHex === 'fdff') {
    await ensurePenRecords()
    finishRecordResponse()
    return
  }
  if (requestHex.startsWith('fd') && obj.data.length === 9) {
    await ensurePenRecords()
    const empty = obj.data.every(item => (item & 0xff) === 0xff)
    handleRecord(responseHex, empty, requestHex)
  }
}

export default {
  name: PEN_TYPE,
  matches: matchesWzbPenResponse,
  handle: handleWzbPenResponse
}
